import { SpeechRecognition } from "@capacitor-community/speech-recognition";
import { PluginListenerHandle } from "@capacitor/core";
import React from "react";

export function useSpeechRecognition(params: { autoStart: boolean }) {
  const [listening, setListening] = React.useState(false);
  const [currentText, setCurrentResult] = React.useState("");
  const lastRecognized = React.useRef("");
  const partialResultListener = React.useRef<PluginListenerHandle>();

  React.useEffect(() => {
    if (params.autoStart) {
      start();
    }
  }, [params.autoStart]);

  React.useEffect(() => {
    SpeechRecognition.addListener("partialResults", (data) => {
      if (data.matches.length > 0) {
        const newText = data.matches[0];
        setCurrentResult((prev) => {
          if (!prev.endsWith(newText)) {
            const fullText = mergeText(prev, lastRecognized.current, newText);
            lastRecognized.current = newText;
            return fullText;
          }
          return prev;
        });
      }
    }).then((handler) => {
      partialResultListener.current = handler;
    });

    return () => {
      if (partialResultListener.current !== undefined) {
        partialResultListener.current.remove();
      }
    };
  }, []);

  React.useEffect(() => {
    SpeechRecognition.requestPermissions();
    SpeechRecognition.addListener("listeningState", (data) => {
      setListening(data.status === "started" ? true : false);
    });

    return () => {
      SpeechRecognition.removeAllListeners();
      SpeechRecognition.isListening().then(({ listening }) => {
        if (listening) {
          SpeechRecognition.stop();
        }
      });
    };
  }, []);

  const start = () => {
    SpeechRecognition.isListening().then(({ listening }) => {
      if (listening) {
        console.warn("Speech recognition started already");
      } else {
        setCurrentResult("");
        SpeechRecognition.start({
          language: "en-US",
          partialResults: true,
          popup: false,
        });
      }
    });
  };

  const stop = () => {
    SpeechRecognition.isListening().then(({ listening }) => {
      if (listening) {
        SpeechRecognition.stop();
      } else {
        console.warn("Speech recognition stopped already");
      }
    });
  };

  return {
    start,
    stop,
    listening,
    currentText,
  };
}

function mergeText(fullText: string, lastRecognized: string, newText: string): string {
  if (!lastRecognized) return newText; // First recognition result

  const oldWords = lastRecognized.split(" ");
  const newWords = newText.split(" ");

  // Find the longest common prefix (LCS) to detect refinements
  let commonIndex = 0;
  while (
    commonIndex < oldWords.length &&
    commonIndex < newWords.length &&
    oldWords[commonIndex] === newWords[commonIndex]
  ) {
    commonIndex++;
  }

  // Check if new text has NO common words with last recognized (indicates a new sentence)
  const hasCommonWords = oldWords.some((word) => newWords.includes(word));

  if (!hasCommonWords) {
    // This is a completely new sentence, lock in previous text and append it
    fullText = `${fullText.trim()}. ${newText}`;
  } else {
    // Otherwise, refine only the last sentence in fullText
    const sentences = fullText.split(". ");
    sentences[sentences.length - 1] = mergeSentence(
      sentences[sentences.length - 1],
      oldWords,
      newWords
    );
    fullText = sentences.join(". ");
  }

  return fullText.trim();
}

/**
 * Refines the last sentence by removing extra words and appending new ones.
 */
function mergeSentence(lastSentence: string, oldWords: string[], newWords: string[]): string {
  let commonIndex = 0;
  while (
    commonIndex < oldWords.length &&
    commonIndex < newWords.length &&
    oldWords[commonIndex] === newWords[commonIndex]
  ) {
    commonIndex++;
  }

  // Remove extra words if the new version is shorter
  const sentenceWords = lastSentence.split(" ");
  while (sentenceWords.length > commonIndex) {
    sentenceWords.pop();
  }

  // Append only new words
  const additionalWords = newWords.slice(commonIndex).join(" ");
  return `${sentenceWords.join(" ")} ${additionalWords}`.trim();
}
